<script setup lang="ts">
import { computed } from 'vue'
import type { BookPageConfig } from '../configs/types'
import { t } from '@/i18n/utils'
import { formatCompactNumber } from '@/utils'

interface Props {
  book: GetContentGetcontentdetailResponse['data']
  config: BookPageConfig
  coverUrl?: string
  introduce?: string
  paragraphs: string[]
  popupTOC: boolean
  bannerShow: boolean
  isInShelf?: boolean
  shelfLoading?: boolean
  currentContentId?: string | null
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (event: 'update:popup-t-o-c', value: boolean): void
  (event: 'goto-tags', tagId: string): void
  (event: 'open-toc'): void
  (event: 'read'): void
  (event: 'back'): void
  (event: 'toggle-shelf'): void
  (event: 'audio'): void
}>()

// 创建可写的 computed 用于 v-model 双向绑定
const popupTOCModel = computed({
  get: () => props.popupTOC,
  set: value => emit('update:popup-t-o-c', value),
})

const hasAudio = computed(() => props.book?.hasAudio === 1)

function onAudioClick() {
  emit('audio')
}
</script>

<template>
  <div>
    <!-- 封面区域 -->
    <div
      class="cover pt-44px pb-44px relative"
      :class="{ 'mt-60px': bannerShow }"
    >
      <!-- 背景模糊封面 -->
      <div
        class="w-full h-full absolute top-0 left-0 !bg-cover !bg-no-repeat"
        :style="`background: url(${coverUrl})`"
      >
        <!-- 返回按钮 -->
        <div
          class="absolute top-12px left-12px text-24px z-99"
          @click="emit('back')"
        >
          <i-icon-park-outline-left class="text-24px" />
        </div>

        <!-- 遮罩层 (固定 0.7 不透明度) -->
        <div class="h-full bg-n-bg-global opacity-70" />

        <!-- 模糊效果 (固定 5px 模糊) -->
        <div
          class="w-full h-full absolute left-0 top-0"
          style="backdrop-filter: blur(5px)"
        />
      </div>

      <!-- 封面内容 -->
      <div class="text-center z-1 relative">
        <img
          class="w-25 h-140px mx-a rounded-3px shadow-lg"
          :src="coverUrl"
          alt="Book Cover"
        >
        <p class="mt-5 text-18px font-bold px-24px">
          {{ book.contentName }}
        </p>
        <p class="mt-5px text-13px text-n-text-1 inline-flex items-center justify-center flex-wrap">
          <span>{{ book.finishStatus ? config.texts.completed : config.texts.ongoing }}｜{{ book.authorInfo?.author }}{{ book.viewUsers ? '｜' : '' }}</span>
          <template v-if="book.viewUsers">
            <i-assets-icon-view-count class="w-13px h-10px flex-shrink-0 mr-2px" />
            <span>{{ formatCompactNumber(book.viewUsers) }}</span>
          </template>
        </p>
      </div>
    </div>

    <!-- Summary 简介卡片 -->
    <div
      class="-mt-8 mx-5 px-5 py-15px rounded relative bg-n-bg-card shadow-[0px_40px_90px_0px_rgba(0,0,0,0.05)]"
    >
      <!-- 标签 -->
      <div class="-mb-10px flex items-center justify-center space-x-10px flex-wrap text-13px">
        <span
          v-for="tag in book.tagList || []"
          :key="tag.id"
          class="mb-10px px-5px py-3px rounded-12px cursor-pointer bg-n-bg-tag text-n-text-2"
          @click="emit('goto-tags', tag.id || '')"
        >
          {{ tag.name }}
        </span>
      </div>

      <!-- 引用装饰 -->
      <div class="py-10px flex items-center space-x-10px">
        <i-icon-park-outline-quote
          class="w-6 h-6"
          :style="{ color: config.theme.quoteColor }"
        />
        <hr
          class="flex-1"
          :style="{ borderColor: config.theme.quoteColor }"
        >
      </div>

      <!-- 简介文本 -->
      <BasicExpandableText
        :text="book.introduce || ''"
        :line-clamp="6"
        text-class="text-14px lh-22px text-n-text-2"
        more-class="text-n-primary cursor-pointer text-14px"
        more-bg-color="white"
      />

      <div class="flex justify-end">
        <i-icon-park-outline-quote
          class="w-6 h-6 rotate-180"
          :style="{ color: config.theme.quoteColor }"
        />
      </div>
    </div>

    <!-- Sample 章节样本 -->
    <div class="mt-5 mx-5 pb-5">
      <div class="flex space-x-4 items-center justify-between">
        <p class="text-17px font-bold whitespace-nowrap text-n-text-1">
          {{ config.texts.catalogue }}
        </p>
        <div class="w-70vw">
          <div
            class="text-13px flex items-center justify-end cursor-pointer text-n-text-2"
            @click="emit('open-toc')"
          >
            <p class="line-clamp-1">
              {{ config.texts.latestChapter }}：{{ book.lastChapter?.chapterName }}
            </p>
            <i-icon-park-outline-right class="w-4 h-4" />
          </div>
        </div>
      </div>

      <!-- 引导语 -->
      <div v-if="introduce" class="lh-30px mt-20px text-n-text-2">
        <p v-html="introduce" />
      </div>

      <!-- 样本文本 -->
      <div class="lh-30px text-n-text-2">
        <p class="mt-4">
          {{ book.firstChapter?.chapterName }}
        </p>
        <p v-for="(item, index) in paragraphs.slice(0, 16)" :key="index" class="mt-4">
          {{ item }}
        </p>
      </div>

      <!-- 分割线 -->
      <div class="mt-10 flex items-center justify-center space-x-2">
        <span class="w-8 h-1px bg-[#999999]" />
        <span class="text-14px text-[#999999]">
          {{ config.texts.readFirstChapter }}
        </span>
        <span class="w-8 h-1px bg-[#999999]" />
      </div>
    </div>

    <!-- TOC 弹窗 -->
    <BizPopupBookTOC
      v-if="book && book.firstChapterContentUrl"
      v-model="popupTOCModel"
      :book="book"
      :content-id="book.id"
      :chapter-id="book.firstChapter?.id"
      :lock-icon-color="config.theme.tocLockColor"
    />

    <!-- 底部固定按钮 -->
    <BasicFixedBottom class="shadow-[0px_-2px_7px_0px_rgba(99,100,100,0.1)]">
      <div class="w-full flex items-center px-5 pt-3 pb-0 bg-n-bg-card gap-15px">
        <div
          class="flex flex-col items-center justify-center min-w-50px py-2px cursor-pointer"
          :class="{ 'opacity-50': shelfLoading }"
          :style="{ color: isInShelf ? 'var(--primary-color)' : '#65686F' }"
          @click="emit('toggle-shelf')"
        >
          <i-assets-book-added-shelf v-if="isInShelf" class="w-22px h-22px pointer-events-none" />
          <i-assets-book-add-shelf v-else class="w-22px h-22px pointer-events-none" />
          <p class="mt-2px text-10px lh-11px">
            {{ isInShelf ? t('BookshelfAdded') : t('Bookshelf') }}
          </p>
        </div>
        <a
          class="flex-1 h-45px lh-45px text-center text-15px font-bold rounded-22.5px cursor-pointer btn-solid text-n-text-inverse"
          @click="emit('read')"
        >
          {{ !book.readFlag ? config.texts.startReading : config.texts.continueReading }}
        </a>
        <!-- 音频入口 -->
        <div
          v-if="hasAudio"
          class="flex flex-col items-center justify-center min-w-50px py-2px cursor-pointer"
          style="color: var(--primary-color)"
          @click="onAudioClick"
        >
          <i-icon-park-outline-headset class="w-22px h-22px" />
          <p class="mt-2px text-10px lh-11px">
            Audio
          </p>
        </div>
      </div>
    </BasicFixedBottom>
  </div>
</template>

<style scoped>
.cover {
  background-size: cover !important;
}
</style>
